/**
 * Nhập danh mục Đại lý/MM từ file Excel theo mẫu — `/middlemen/import` (Doc/09 mục 5).
 *
 * Ba bước: tải mẫu → chọn file → xem kết quả kiểm từng dòng → nhập. File được ĐỌC NGAY TRONG
 * TRÌNH DUYỆT (`ui/sheet-reader.js`) — không gửi file lên máy chủ; máy chủ nhận các dòng JSON,
 * kiểm lại và nhập trong một giao dịch (còn dòng lỗi là không nhập dòng nào).
 *
 * Chỉ điều phối viên trở lên — cùng quyền với "Thêm MM"; máy chủ là nơi chặn thật (R7).
 */
import * as api from '../api-client.js';
import { buildHash, downloadBlob, esc, navigate, toast } from '../ui/util.js';
import { readSpreadsheet, rowsToMiddlemen } from '../ui/sheet-reader.js';
import { middlemanTypeLabel } from './middlemen.js';

const FIELD_LABEL = { code: 'Mã', name: 'Tên', type: 'Loại', commune: 'Xã' };
const STATUS = {
  create: { text: 'Sẽ tạo', cls: 'status-approved' },
  skip: { text: 'Bỏ qua', cls: 'status-draft' },
  error: { text: 'Lỗi', cls: 'status-rejected' },
};

export async function renderMiddlemanImport(container) {
  const user = api.getCurrentUser();
  let donVis = null;
  if (user?.role === 'admin') {
    try {
      donVis = (await api.instances.list()).filter((d) => d.isActive);
    } catch {
      donVis = [];
    }
  }

  container.innerHTML = `
    <div class="page-head">
      <div><h1>Nhập danh mục Đại lý / MM</h1>
        <p>Tải file mẫu, điền mỗi dòng một MM (Mã, Tên, Loại, Tỉnh, Xã, Địa chỉ), rồi chọn file để kiểm tra.
           Mã đã có trong danh mục sẽ được bỏ qua. Còn dòng lỗi thì chưa nhập dòng nào.</p></div>
      <div class="page-head-actions">
        <a class="btn btn-text" href="${buildHash('/middlemen')}"><span class="ms">arrow_back</span>Danh mục MM</a>
      </div>
    </div>
    <div class="card">
      ${
        donVis
          ? `<div class="form-row"><label for="imInstance">Đơn vị điều tra *</label>
               <select id="imInstance"><option value="">— Chọn —</option>
                 ${donVis.map((d) => `<option value="${esc(d.id)}">${esc(d.name)}</option>`).join('')}</select></div>`
          : ''
      }
      <div class="form-actions" style="justify-content:flex-start">
        <button class="btn btn-tonal" type="button" id="imTemplate"><span class="ms">download</span>Tải file mẫu</button>
        <label class="btn" for="imFile"><span class="ms">upload_file</span>Chọn file .xlsx / .csv</label>
        <input type="file" id="imFile" accept=".xlsx,.csv" hidden>
      </div>
      <p class="hint-text" id="imFileName"></p>
    </div>
    <div id="imResult"></div>`;

  const instanceSel = container.querySelector('#imInstance');
  const resultEl = container.querySelector('#imResult');
  const instanceParams = () => (instanceSel?.value ? { instanceId: instanceSel.value } : {});
  const needInstance = () => {
    if (instanceSel && !instanceSel.value) {
      toast('Chọn đơn vị điều tra trước');
      return true;
    }
    return false;
  };

  container.querySelector('#imTemplate').addEventListener('click', async () => {
    if (needInstance()) return;
    try {
      downloadBlob(await api.middlemen.importTemplate(instanceParams()), 'mau-nhap-dai-ly-mm.xlsx');
    } catch (err) {
      toast(`Không tải được file mẫu: ${err.message}`);
    }
  });

  let records = [];

  container.querySelector('#imFile').addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // chọn lại đúng file đó (sau khi sửa) vẫn chạy lại
    if (!file) return;
    container.querySelector('#imFileName').textContent = `File: ${file.name}`;
    if (needInstance()) return;
    resultEl.innerHTML = '<div class="empty-state">Đang đọc file...</div>';
    try {
      const { records: recs, missing } = rowsToMiddlemen(await readSpreadsheet(await file.arrayBuffer(), file.name));
      if (missing.length) {
        resultEl.innerHTML = `<div class="status-banner tone-error"><span class="ms">error</span>
          <span>File thiếu cột: <b>${missing.map((f) => esc(FIELD_LABEL[f] || f)).join(', ')}</b>. Dùng file mẫu hoặc đổi tên tiêu đề cột cho khớp.</span></div>`;
        return;
      }
      if (!recs.length) {
        resultEl.innerHTML = '<div class="empty-state">File không có dòng dữ liệu nào dưới dòng tiêu đề.</div>';
        return;
      }
      records = recs;
      renderResult(await api.middlemen.importRows({ ...instanceParams(), dryRun: true, rows: records }));
    } catch (err) {
      resultEl.innerHTML = `<div class="status-banner tone-error"><span class="ms">error</span><span>${esc(err.message)}</span></div>`;
    }
  });

  function renderResult(kq) {
    const { summary, rows } = kq;
    const canImport = summary.error === 0 && summary.create > 0;
    resultEl.innerHTML = `
      <div class="card">
        <h2 class="card-title"><span class="ms">fact_check</span>Kết quả kiểm tra: ${summary.total} dòng</h2>
        <p>${summary.create} sẽ tạo · ${summary.skip} bỏ qua (mã đã có) · <b>${summary.error} lỗi</b></p>
        ${summary.error ? '<div class="status-banner tone-error"><span class="ms">error</span><span>Sửa các dòng lỗi trong file rồi chọn lại file — chưa nhập dòng nào.</span></div>' : ''}
        <div class="table-scroll"><table class="list-table">
          <thead><tr><th>Dòng</th><th>Trạng thái</th><th>Mã</th><th>Tên</th><th>Loại</th><th>Địa bàn</th><th>Ghi chú</th></tr></thead>
          <tbody>${rows
            .map(
              (r) => `<tr>
                <td>${r.row}</td>
                <td><span class="badge ${STATUS[r.status].cls}">${STATUS[r.status].text}</span></td>
                <td class="mono">${esc(r.code || '—')}</td>
                <td>${esc(r.name || '—')}</td>
                <td>${esc(r.type ? middlemanTypeLabel(r.type) : '—')}</td>
                <td>${esc(r.regionName || '—')}</td>
                <td>${esc(r.message || '')}</td>
              </tr>`,
            )
            .join('')}</tbody>
        </table></div>
        <div class="form-actions">
          <button class="btn" type="button" id="imCommit" ${canImport ? '' : 'disabled'}>
            <span class="ms">playlist_add</span>Nhập ${summary.create} MM</button>
        </div>
      </div>`;
    resultEl.querySelector('#imCommit')?.addEventListener('click', async (ev) => {
      ev.currentTarget.disabled = true;
      try {
        const done = await api.middlemen.importRows({ ...instanceParams(), dryRun: false, rows: records });
        toast(`Đã nhập ${done.summary.create} MM`);
        navigate(buildHash('/middlemen'));
      } catch (err) {
        toast(`Không nhập được: ${err.message}`);
        ev.currentTarget.disabled = false;
      }
    });
  }
}
